import { Component, OnInit, signal, ViewChild } from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { LayoutService } from '../service/layout.service';
import { SplashScreenService } from '../service/splash-screen.service';
import { ThemeService } from '../service/theme.service';
import { MatSidenav, MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { MatProgressBar, MatProgressBarModule } from '@angular/material/progress-bar';
import { ToastGroupComponent } from 'src/app/shared/components/toast-alert/toastGroup/toast-group.component';
import { AsyncPipe, CommonModule } from '@angular/common';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { ToasService } from 'src/app/shared/components/toast-alert/service/toas-service.service';
import { SafariBannerComponent } from './components/safari-banner/safari-banner.component';
import { ToolbarComponent } from './components/toolbar/toolbar.component';
import { switchMap, take } from 'rxjs';
import { LinkNotificacion, Message, Notification } from '../interface/notification.interface';
import { NotificationService } from '../service/notification.service';
import { encriptar } from 'src/app/shared/helpers/encryption';
import { environment as env, environment } from 'src/environments/environment';
import { ToastInitConfig } from 'src/app/shared/components/toast-alert/toast.config';

@Component({
  selector: 'mg-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true,
  imports: [AsyncPipe, MatProgressBar, MatSidenavModule, ToastGroupComponent, RouterOutlet, SidenavComponent, MatProgressBarModule, CommonModule, SafariBannerComponent, ToolbarComponent]
})
export class LayoutComponent implements OnInit {
  loading = signal<boolean>(false);
  isDesktop$;
  sidenavCollapsed$;
  notificationsShowed = signal<boolean>(false);

  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild(MatSidenavContainer) sidenavContainer!: MatSidenavContainer;

  private notificationsLoading = false;

  constructor(
    private readonly _layoutService: LayoutService,
    private readonly _router: Router,
    private readonly _splashScreenService: SplashScreenService,
    private readonly _notificationService: NotificationService,
    private readonly _toastService: ToasService
  ) {
    this.isDesktop$ = this._layoutService.isScreenXL$;
    this.sidenavCollapsed$ = this._layoutService.sidenavCollapsed$;
    this._splashScreenService.hide();
    this.listenRouteChanges();
  }

  ngOnInit(): void {
    this.loadNotifications(true);

    this._notificationService.triggerShow$.subscribe(() => this.loadNotifications(false));

    this._toastService.toastObs.subscribe(toasts => {
      if (Object.keys(toasts).length === 0) this.notificationsLoading = false;
    });
  }

  clickItem(): void {
    this.isDesktop$.subscribe(x => { if (!x) this.sidenav.toggle(); });
  }

  toggleSidenavCollapse(collapsed: boolean): void {
    collapsed ? this._layoutService.collapseSidenav() : this._layoutService.expandSidenav();
    setTimeout(() => this.sidenavContainer.updateContentMargins(), 50);
  }

  private loadNotifications(isInitial: boolean): void {
    if (!isInitial && this.notificationsLoading) return;

    this.notificationsLoading = true;

    this._notificationService.getNotifications().pipe(take(1)).subscribe({
      next: (val) => {
        if (val?.isSuccess) {
          const unreadItems = val.data.filter((item: Notification) => !item.bIsRead);
          this._notificationService.hasUnread$.next(unreadItems.length > 0);
          this._toastService.initBatch(this.mapNotificationsToToast(unreadItems));
          if (isInitial) this.notificationsShowed.set(true);
        }
        if (!val?.isSuccess || isInitial) this.notificationsLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.notificationsLoading = false;
      }
    });
  }

  private mapNotificationsToToast(items: Notification[]): ToastInitConfig[] {
    //console.log(items)
    return items.map(item => ({
      type: 'warning',
      title: (item.sMensaje as Message)?.title ?? '',
      message: (item.sMensaje as Message)?.message ?? '',
      labelCheck: 'No volver a mostrar',
      dateCreation: item.dFecha_Creacion,
      notificationId: item.nId_Notificacion,
      timer: 20,
      sUrlredirection: this.buildNotificationUrl(item.sLink_Notificacion)
    }));
  }

  private buildNotificationUrl(link: LinkNotificacion): string {
    if (!link?.base) return '';
    
    const queryParams = Object.entries(link.params)
      .map(([key, value]) => 
        `${encodeURIComponent(encriptar(env.securityKey, key))}=${encodeURIComponent(encriptar(env.securityKey, String(value)))}`
      )
      .join('&');

    return queryParams ? `${link.base}?${queryParams}` : link.base;
  }
    private listenRouteChanges(): void {
      this._router.events.subscribe(event => {
        if (event instanceof NavigationStart) this.loading.set(true);  
        if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError)
          this.loading.set(false);  
      });
    }

  private markAsReadAndUpdateBadge(notificationId: number): void {
    this._notificationService.markNotificationsAsRead([notificationId]).pipe(
      take(1),
      switchMap(() => this._notificationService.getNotifications().pipe(take(1)))
    ).subscribe({ next: (val) => this.updateUnreadBadge(val) });
  }

  private updateUnreadBadge(val: any): void {
    if (!val?.isSuccess) return;
    this._notificationService.hasUnread$.next(val.data.some((n: Notification) => !n.bIsRead));
  }

  private navigateToLink(link: LinkNotificacion): void {
    const encryptedParams = Object.fromEntries(
      Object.entries(link.params).map(([k, v]) => [
        encriptar(env.securityKey, k),
        encriptar(env.securityKey, String(v))
      ])
    );
    this._router.navigate([link.base], { queryParams: encryptedParams });
  }
}